import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import { LegacyTypography } from '../../../theme/LegacyTypography.es.js';
import 'react';
import { LegacyColorMap } from '../../../theme/LegacyColorMap.es.js';
import { Avatar } from '../../display/Avatar/Avatar.es.js';
import 'clsx';
import { breakpoints, remapDeprecatedColors } from '../TestimonialSolo/TestimonialMapping.es.js';
import { TestimonialSolo } from '../TestimonialSolo/TestimonialSolo.es.js';
import { boxSizing } from '../../../style-mixins/reset.es.js';

const CommonStyles = labelStyles("TestimonialCommon", {
  quote: css`
    ${boxSizing};
    ${LegacyTypography.smallPrint1}
    color: ${Colors.GrayWarm10};
    margin-bottom: 10px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-bottom: 20px;
      ${LegacyTypography.paragraph3}
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
      margin-bottom: 20px;
      ${LegacyTypography.paragraph1}
      color: ${Colors.GrayWarm9};
    }
  `,
  author: css`
    ${boxSizing};
    ${LegacyTypography.smallPrint1Bold}
    margin: 0;
    color: ${Colors.GrayWarm9};

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${LegacyTypography.paragraph3Bold}
    }
  `,
  company: css`
    ${boxSizing};
    ${LegacyTypography.smallPrint1}
    margin: 0;
    color: ${Colors.GrayWarm9};

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${LegacyTypography.paragraph3}
    }
  `
});
const TwoColumnStyles = labelStyles("TestimonialTwoColumn", {
  outerBox: (backgroundColor = "neutral100") => css`
    ${boxSizing};
    background-color: ${Colors[LegacyColorMap[remapDeprecatedColors(backgroundColor)]]};
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 72px 30px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      padding: 82px 0px;
    }
  `,
  grid: css`
    ${boxSizing};
    display: grid;
    grid-template-rows: auto;
    row-gap: 25px;
    max-width: 1170px;
    justify-content: center;
    margin: 0 auto;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      grid-template-columns: auto 1px auto;
      column-gap: 15px;
      @media screen and (min-width: ${breakpoints.desktop}px) {
        column-gap: 115px;
      }
    }
  `,
  content: css`
    ${boxSizing};
    margin: 0 auto;
    text-align: center;
    max-width: 336px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 339px;
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
      max-width: 370px;
    }
  `,
  avatarContainer: css`
    ${boxSizing};
    margin-bottom: 10px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-bottom: 20px;
    }
  `,
  border: css`
    @media screen and (min-width: ${breakpoints.tablet}px) {
      width: 1px;
      background-color: ${Colors.GrayWarm4};
    }
  `
});
const ThreeColumnStyles = labelStyles("TestimonialThreeColumn", {
  outerBox: (backgroundColor = "neutral100") => css`
    ${boxSizing};
    background-color: ${Colors[LegacyColorMap[remapDeprecatedColors(backgroundColor)]]};
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 40px 0px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      padding: 107px 0px;
    }
  `,
  grid: css`
    ${boxSizing};
    display: grid;
    grid-template-rows: auto;
    row-gap: 40px;
    max-width: 1170px;
    justify-content: center;
    margin: 0 auto;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      row-gap: 50px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      grid-template-columns: auto auto auto;
      column-gap: 30px;
    }
  `,
  content: css`
    ${boxSizing};
    margin: 0 auto;
    text-align: left;
    max-width: 336px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 462px;
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
      max-width: 370px;
    }
  `,
  innerFlex: css`
    ${boxSizing};
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 20px;
  `
});
const renderContentForTwoColumn = (props, index) => {
  return jsx(
    "div",
    { css: TwoColumnStyles.content, "data-zds": true },
    props.testimonials[index].hasAuthorAvatar && jsx(
      "div",
      { css: TwoColumnStyles.avatarContainer, "data-zds": true },
      jsx(Avatar, { "data-testId": "Avatar", name: props.testimonials[index].author, url: props.testimonials[index].avatarUrl, size: "medium" })
    ),
    jsx("div", { css: CommonStyles.quote, "data-zds": true }, props.testimonials[index].quote),
    jsx("p", { css: CommonStyles.author, "data-zds": true }, props.testimonials[index].author),
    jsx("p", { css: CommonStyles.company, "data-zds": true }, props.testimonials[index].company)
  );
};
const renderTwoColumn = (props) => {
  return jsx(
    "div",
    { css: TwoColumnStyles.outerBox(props.backgroundColor), "data-zds": true },
    jsx(
      "div",
      { css: TwoColumnStyles.grid, "data-zds": true },
      renderContentForTwoColumn(props, 0),
      jsx("div", { css: TwoColumnStyles.border, "data-zds": true }),
      renderContentForTwoColumn(props, 1)
    )
  );
};
const renderThreeColumn = (props) => {
  return jsx(
    "div",
    { css: ThreeColumnStyles.outerBox(props.backgroundColor), "data-zds": true },
    jsx("div", { css: ThreeColumnStyles.grid, "data-zds": true }, props.testimonials.slice(0, props.numberOfColumns).map((testimonial, index) => jsx(
      "div",
      { key: index, css: ThreeColumnStyles.content, "data-zds": true },
      jsx("div", { css: CommonStyles.quote, "data-zds": true }, testimonial.quote),
      testimonial.hasAuthorAvatar ? jsx(
        "div",
        { css: ThreeColumnStyles.innerFlex, "data-zds": true },
        jsx(
          "div",
          { css: TwoColumnStyles.avatarContainer, "data-zds": true },
          jsx(Avatar, { "data-testId": "Avatar", name: testimonial.author, url: testimonial.avatarUrl, size: "medium" })
        ),
        jsx(
          "div",
          { "data-zds": true },
          jsx("p", { css: CommonStyles.author, "data-zds": true }, testimonial.author),
          jsx("p", { css: CommonStyles.company, "data-zds": true }, testimonial.company)
        )
      ) : jsx(
        "div",
        { "data-zds": true },
        jsx("p", { css: CommonStyles.author, "data-zds": true }, testimonial.author),
        jsx("p", { css: CommonStyles.company, "data-zds": true }, testimonial.company)
      )
    )))
  );
};
const TestimonialMultipleColumn = (props) => {
  if (props.testimonials.length === 1) {
    const testimonial = props.testimonials[0];
    return jsx(TestimonialSolo, { backgroundColor: props.backgroundColor, avatarUrl: testimonial.avatarUrl, company: testimonial.company, quote: testimonial.quote, author: testimonial.author });
  }
  const renderType = props.numberOfColumns === 2 ? renderTwoColumn : renderThreeColumn;
  return renderType(props);
};

export { TestimonialMultipleColumn };
//# sourceMappingURL=TestimonialMultipleColumn.es.js.map
